import React, { useState, useEffect } from 'react';

const Home = () => {
  const [isArabic, setIsArabic] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load custom Arabic fonts
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Black.ttf') format('truetype');
        font-weight: 900;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Bold.ttf') format('truetype');
        font-weight: 700;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Medium.ttf') format('truetype');
        font-weight: 500;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Regular.ttf') format('truetype');
        font-weight: 400;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Light.ttf') format('truetype');
        font-weight: 300;
        font-style: normal;
      }
    `;
    document.head.appendChild(style);
  }, []);

  const translations = {
    'Al-Irda Media': 'مؤسسة إرضاء الإعلامية',
    'Official media platform of Al-Irda Initiative': 
    'المنصة الإعلامية الرسمية لمؤسسة إرضاء',
    'Home': 'الرئيسية',
    'News': 'الأخبار',
    'Reports': 'التقارير',
    'Media Center': 'المركز الإعلامي',
    'Welcome to Al-Irda Media': 'مرحباً بكم في مؤسسة إرضاء الإعلامية',
    'Welcome to the official media platform of Al-Irda Initiative. Your trusted source for news and updates.':
    'نرحب بكم في المنصة الإعلامية الرسمية لمؤسسة إرضاء. مصدركم الموثوق للأخبار والتحديثات.',
    'Media Services': 'خدماتنا الإعلامية',
    'News Coverage': 'التغطية الإخبارية',
    'Press Releases': 'البيانات الصحفية',
    'Documentaries': 'الأفلام الوثائقية',
    'Field Reports': 'التقارير الميدانية',
    'Truth and credibility are our commitment': 'الحق والمصداقية التزامنا',
    'Al-Irda Initiative': 'مؤسسة إرضاء',
    'Public service platform serving the community': 'منصة الخدمة العامة لخدمة المجتمع'
  };

  const translateText = (text) => isArabic ? (translations[text] || text) : text;

  const toggleLanguage = () => setIsArabic(!isArabic);

  // MODIFIED: Always use the Arabic font, regardless of language
  const getFontFamily = (weight = 400) => {
    return `'ITF Qomra Arabic', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif`;
  };

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #F0F0F0 0%, #FFFFFF 50%, #F0F0F0 100%)',
      padding: '0',
      fontFamily: getFontFamily(),
      direction: isArabic ? 'rtl' : 'ltr',
      position: 'relative',
      overflow: 'hidden',
      fontSize: '16px',
      lineHeight: '1.6',
      minHeight: '100vh'
    },
    navbar: {
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      padding: '1rem 2rem',
      boxShadow: '0 4px 20px rgba(50, 55, 59, 0.1)',
      marginBottom: '0',
      position: 'fixed',
      top: '0',
      width: '100%',
      zIndex: '1000',
      backdropFilter: 'blur(10px)',
      borderBottom: `1px solid #D3BA80`,
      transition: 'all 0.3s ease'
    },
    navContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    logo: {
      color: '#255853',
      fontSize: '1.8rem',
      fontWeight: '700',
      textDecoration: 'none',
      textShadow: '1px 1px 2px rgba(37, 88, 83, 0.2)',
      letterSpacing: '-0.5px',
      fontFamily: getFontFamily(700)
    },
    navLinks: {
      display: 'flex',
      gap: '2rem'
    },
    navLink: {
      color: '#32373B',
      textDecoration: 'none',
      padding: '0.75rem 1.25rem',
      borderRadius: '25px',
      transition: 'all 0.3s ease',
      fontWeight: '600',
      fontSize: '1rem',
      border: '2px solid transparent',
      fontFamily: getFontFamily(500),
      '&:hover': {
        backgroundColor: '#357E77',
        color: '#FFFFFF'
      }
    },
    languageToggle: {
      position: 'fixed',
      top: '100px',
      right: isArabic ? 'auto' : '20px',
      left: isArabic ? '20px' : 'auto',
      zIndex: '1000'
    },
    toggleButton: {
      backgroundColor: '#255853',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '25px',
      padding: '10px 20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 88, 83, 0.3)',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontFamily: getFontFamily(500)
    },
    header: {
      textAlign: 'center',
      color: '#32373B',
      padding: '160px 20px 80px',
      background: `linear-gradient(135deg, 
        rgba(211, 186, 128, 0.1) 0%, 
        rgba(211, 186, 128, 0.2) 50%, 
        rgba(255, 255, 255, 0) 100%)`,
      position: 'relative',
      overflow: 'hidden',
      borderBottom: `3px solid #D3BA80`
    },
    title: {
      fontSize: '3rem',
      fontWeight: '700',
      marginBottom: '1.5rem',
      color: '#255853',
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      letterSpacing: '-1px',
      lineHeight: '1.2',
      position: 'relative',
      fontFamily: getFontFamily(700)
    },
    titleAccent: {
      display: 'block',
      width: '80px',
      height: '4px',
      background: 'linear-gradient(90deg, #D3BA80, #E0CFA6)',
      margin: '1rem auto',
      borderRadius: '2px'
    },
    subtitle: {
      fontSize: '1.3rem',
      marginBottom: '2rem',
      color: '#357E77',
      fontWeight: '500',
      maxWidth: '800px',
      margin: '0 auto 2rem',
      lineHeight: '1.5',
      fontFamily: getFontFamily(500)
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      padding: '3rem 2rem',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      backdropFilter: 'blur(5px)',
      border: `1px solid #D3BA80`,
      marginBottom: '3rem'
    },
    section: {
      marginBottom: '4rem',
      padding: '2rem',
      borderRadius: '12px',
      backgroundColor: '#FFFFFF',
      boxShadow: '0 8px 24px rgba(50, 55, 59, 0.05)',
      border: `1px solid #E0CFA6`,
      position: 'relative',
      overflow: 'hidden'
    },
    sectionTitle: {
      fontSize: '2.2rem',
      fontWeight: '700',
      color: '#255853',
      marginBottom: '2rem',
      textAlign: 'center',
      borderBottom: `2px solid #D3BA80`,
      paddingBottom: '1rem',
      textTransform: 'uppercase',
      letterSpacing: '-0.5px',
      fontFamily: getFontFamily(700)
    },
    cardContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '2rem',
      marginTop: '2rem',
      width: '100%',
      justifyContent: 'center',
      justifyItems: 'center'
    },
    card: {
      backgroundColor: '#FFFFFF',
      padding: '2rem 1.5rem',
      borderRadius: '12px',
      textAlign: 'center',
      border: `2px solid #E0CFA6`,
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '280px',
      width: '100%',
      maxWidth: '320px'
    },
    cardHover: {
      transform: 'translateY(-8px) scale(1.02)',
      boxShadow: '0 15px 30px rgba(53, 126, 119, 0.15)',
      borderColor: '#357E77',
      backgroundColor: '#F0F0F0'
    },
    cardTitle: {
      fontSize: '1.4rem',
      fontWeight: '600',
      color: '#32373B',
      marginBottom: '1rem',
      textTransform: 'uppercase',
      letterSpacing: '-0.3px',
      textAlign: 'center',
      fontFamily: getFontFamily(600)
    },
    mottoSection: {
      textAlign: 'center',
      background: 'linear-gradient(135deg, #255853 0%, #32373B 100%)',
      padding: '4rem 2rem',
      borderRadius: '12px',
      margin: '3rem 0',
      position: 'relative',
      overflow: 'hidden'
    },
    mottoText: {
      fontSize: '2.2rem',
      fontWeight: '700',
      color: '#FFFFFF',
      fontStyle: 'italic',
      margin: 0,
      textShadow: '0 3px 10px rgba(0,0,0,0.3)',
      lineHeight: '1.3',
      fontFamily: getFontFamily(700)
    },
    footer: {
      textAlign: 'center',
      marginTop: '3rem',
      padding: '2rem 1rem',
      color: '#32373B',
      borderTop: `2px solid #D3BA80`,
      backgroundColor: 'rgba(240, 240, 240, 0.8)',
      fontWeight: '600',
      fontSize: '1rem',
      fontFamily: getFontFamily(500)
    },
    welcomeSection: {
      textAlign: isArabic ? 'right' : 'left',
      direction: isArabic ? 'rtl' : 'ltr',
      border: `3px solid #D3BA80`,
      borderLeft: isArabic ? 'none' : '3px solid #D3BA80',
      borderRight: isArabic ? '3px solid #D3BA80' : 'none',
      padding: isArabic ? '0 1.5rem 0 0' : '0 0 0 1.5rem',
      backgroundColor: 'rgba(211, 186, 128, 0.05)',
      padding: '2rem',
      borderRadius: '8px',
      marginBottom: '0'
    },
    paragraph: {
      fontSize: '1.1rem',
      lineHeight: '1.7',
      color: '#32373B',
      fontWeight: '400',
      margin: '0',
      fontFamily: getFontFamily(400)
    },
    cardDescription: {
      color: '#357E77',
      margin: '0.75rem 0 0',
      fontWeight: '500',
      fontSize: '1rem',
      lineHeight: '1.5',
      textAlign: 'center',
      fontFamily: getFontFamily(500)
    },
    floatingShapes: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      pointerEvents: 'none'
    },
    shape: {
      position: 'absolute',
      background: 'rgba(211, 186, 128, 0.08)',
      borderRadius: '50%'
    },
    servicesWrapper: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%'
    },
    initiativeInfo: {
      textAlign: 'center',
      padding: '2rem',
      backgroundColor: 'rgba(211, 186, 128, 0.08)',
      borderRadius: '12px',
      marginBottom: '2rem',
      border: `1px solid #E0CFA6`
    },
    initiativeTitle: {
      color: '#255853',
      fontSize: '1.5rem',
      fontWeight: '700',
      marginBottom: '1rem',
      fontFamily: getFontFamily(700)
    }
  };

  const mediaServices = [
    { title: 'التغطية الإخبارية', enTitle: 'News Coverage', icon: '📰', description: 'تغطية شاملة لأنشطة مؤسسة إرضاء' },
    { title: 'البيانات الصحفية', enTitle: 'Press Releases', icon: '📢', description: 'إصدار البيانات الرسمية للمؤسسة' },
    { title: 'الأفلام الوثائقية', enTitle: 'Documentaries', icon: '🎬', description: 'إنتاج أفلام وثائقية عن أعمال المؤسسة' },
    { title: 'التقارير الميدانية', enTitle: 'Field Reports', icon: '📡', description: 'تقارير مباشرة من مشاريع المؤسسة' }
  ];

  return (
    <div style={styles.container}>
      {/* Floating Background Shapes */}
      <div style={styles.floatingShapes}>
        <div style={{...styles.shape, width: '150px', height: '150px', top: '10%', left: '5%', background: 'rgba(211, 186, 128, 0.05)'}}></div>
        <div style={{...styles.shape, width: '120px', height: '120px', top: '60%', right: '10%', background: 'rgba(53, 126, 119, 0.08)'}}></div>
        <div style={{...styles.shape, width: '80px', height: '80px', bottom: '20%', left: '15%', background: 'rgba(224, 207, 166, 0.06)'}}></div>
      </div>

      {/* Language Toggle Button */}
      <div style={styles.languageToggle}>
        <button 
          onClick={toggleLanguage}
          style={styles.toggleButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#D3BA80';
            e.target.style.transform = 'translateY(-3px) scale(1.05)';
            e.target.style.boxShadow = '0 8px 20px rgba(211, 186, 128, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#255853';
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 15px rgba(37, 88, 83, 0.3)';
          }}
        >
          {isArabic ? 'ENGLISH' : 'العربية'}
        </button>
      </div>

      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.title}>{translateText('Al-Irda Media')}</h1>
        <div style={styles.titleAccent}></div>
        <p style={styles.subtitle}>
          {translateText('Official media platform of Al-Irda Initiative')}
        </p>
      </header>

      {/* Main Content */}
      <div style={styles.content}>
        {/* مؤسسة إرضاء Info */}
        <div style={styles.initiativeInfo}>
          <h3 style={styles.initiativeTitle}>{translateText('Al-Irda Initiative')}</h3>
          <p style={styles.paragraph}>
            {translateText('Public service platform serving the community')}
          </p>
        </div>

        {/* Welcome Section */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{translateText('Welcome to Al-Irda Media')}</h2>
          <div style={styles.welcomeSection}>
            <p style={styles.paragraph}>
              {translateText('Welcome to the official media platform of Al-Irda Initiative. Your trusted source for news and updates.')}
            </p>
          </div>
        </section>

        {/* Media Services Section */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{translateText('Media Services')}</h2>
          <div style={styles.servicesWrapper}>
            <div style={styles.cardContainer}>
              {mediaServices.map((service, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.card,
                    ...(hoveredCard === index ? styles.cardHover : {})
                  }}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div style={{fontSize: '3rem', marginBottom: '1.5rem'}}>{service.icon}</div>
                  <h3 style={styles.cardTitle}>
                    {isArabic ? service.title : service.enTitle}
                  </h3>
                  <p style={styles.cardDescription}>
                    {service.description}
                  </p>
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    width: '100%',
                    height: '4px',
                    background: 'linear-gradient(90deg, #255853, #D3BA80)',
                    transform: hoveredCard === index ? 'scaleX(1)' : 'scaleX(0)',
                    transition: 'transform 0.3s ease',
                    transformOrigin: 'left'
                  }}></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Media Motto Section */}
        <div style={styles.mottoSection}>
          <p style={styles.mottoText}>"الحق والمصداقية التزامنا"</p>
          <p style={{...styles.mottoText, fontSize: '1.5rem', marginTop: '1.5rem', opacity: '0.9'}}>
            "Truth and credibility are our commitment"
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer style={styles.footer}>
        <p>{translateText('Truth and credibility are our commitment')}</p>
        <p style={{fontSize: '0.9rem', marginTop: '0.5rem', opacity: '0.8'}}>
          {translateText('Al-Irda Initiative')} - {translateText('Public service platform serving the community')}
        </p>
      </footer>
    </div>
  );
};

export default Home;